// Enums
import { FEATURE_FLAGS } from '@/enums/FeatureFlags';

// Types
import AppRegion from '@/types/app-region';
import Nullable from '@/types/nullable';
import PortfolioType from '@/types/portfolio-type';
import User from '@/types/user';

// Utils
import { isFFEnabled } from '@/utils/feature-flag';
import { isModelType } from '@/utils/portfolios';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '@/utils/permissions';
import { validateUserAppRegion } from '@/utils/users';

/**
 * Feature access functions that combine permission checks, feature flags,
 * and other conditions to determine if a user can access a specific feature.
 *
 * This centralized approach prevents "shotgun surgery" when access logic changes.
 */

/**
 * Determines if the user can access Canada-specific Tax Analysis features.
 *
 * This is used to gate Canada-specific tax calculation features such as capital gains calculations
 * and tax reporting that follow Canadian tax regulations.
 */
export const canAccessTaxAnalysisCanada = (user?: Nullable<User>): boolean => {
  if (!user) {
    return false;
  }

  return (
    validateUserAppRegion(user as User, AppRegion.CAN) &&
    userHasPermission(user, PERMISSIONS.TAX_ANALYSIS) &&
    isFFEnabled(FEATURE_FLAGS.TAX_ANALYSIS_CA_ENABLED)
  );
};

/**
 * Determines if the user can access US-specific Tax Analysis features.
 *
 * This is used to gate US-specific tax calculation features such as tax lot tracking,
 * cost basis reporting, and capital gains calculations that follow US tax regulations.
 */
export const canAccessTaxAnalysisUnitedStates = (user?: Nullable<User>): boolean => {
  if (!user) {
    return false;
  }

  return (
    validateUserAppRegion(user as User, AppRegion.US) && userHasPermission(user, PERMISSIONS.TAX_ANALYSIS) && isFFEnabled(FEATURE_FLAGS.TAX_ANALYSIS_US_ENABLED)
  );
};

/**
 * Determines if the user can access Tax Analysis features for any region.
 * This is a convenience function that returns true if the user has access to either
 * Canada-specific OR US-specific tax analysis features, and the portfolio context allows it.
 */
export const canAccessTaxAnalysis = (user?: Nullable<User>, portfolioType?: PortfolioType): boolean => {
  if (!user) {
    return false;
  }

  // Model portfolios (TEMPLATE/FIRM) should not have tax analysis
  if (portfolioType && isModelType(portfolioType)) {
    return false;
  }

  return canAccessTaxAnalysisCanada(user) || canAccessTaxAnalysisUnitedStates(user);
};

/**
 * Determines if the user can access Multi-Portfolio Statement Reader feature.
 * Requires both the PORTFOLIOS_AI_STATEMENT_READER permission and MULTI_PORTFOLIOS_STATEMENT_READER_ENABLED feature flag.
 */
export const canAccessMultiPortfoliosStatementReader = (user?: Nullable<User>): boolean => {
  if (!user) {
    return false;
  }

  return userHasPermission(user, PERMISSIONS.PORTFOLIOS_AI_STATEMENT_READER) && isFFEnabled(FEATURE_FLAGS.MULTI_PORTFOLIOS_STATEMENT_READER_ENABLED);
};

/**
 * Determines if the user can access CSV file upload for portfolio import.
 * This is mutually exclusive with AI Statement Reader - users with PORTFOLIOS_FILE_IMPORT permission
 * can only access CSV upload if they do NOT have PORTFOLIOS_AI_STATEMENT_READER permission.
 */
export const canAccessUploadCSV = (user?: Nullable<User>): boolean => {
  if (!user) {
    return false;
  }

  return userHasPermission(user, PERMISSIONS.PORTFOLIOS_FILE_IMPORT) && !userHasPermission(user, PERMISSIONS.PORTFOLIOS_AI_STATEMENT_READER);
};

/**
 * Determines if the user can access AI statement reader for portfolio import.
 * Requires the PORTFOLIOS_AI_STATEMENT_READER permission.
 * Note: CSV upload access will be hidden if this permission is present (enforced by canAccessUploadCSV).
 */
export const canAccessAIStatementReader = (user?: Nullable<User>): boolean => {
  if (!user) {
    return false;
  }

  return userHasPermission(user, PERMISSIONS.PORTFOLIOS_AI_STATEMENT_READER);
};

/**
 * Determines if the user can access the Allocation Mode Toggle in the portfolio editor.
 * The toggle allows switching between weight-based and units-based allocation modes for portfolio holdings.
 *
 * Access is granted if the user has at least one of the following permissions:
 * - PORTFOLIO_UNITS_MODE - Enables units-based allocation mode
 * - PORTFOLIOS_FILE_IMPORT - Enables CSV file upload for portfolios
 * - PORTFOLIOS_AI_STATEMENT_READER - Enables AI statement reading
 * - TAX_ANALYSIS - Enables tax analysis feature
 *
 * @param user - The user to check permissions for
 * @returns true if the user can access the allocation mode toggle, false otherwise
 */
export const canAccessAllocationModeToggle = (user?: Nullable<User>): boolean => {
  if (!user) {
    return false;
  }

  return userHasPermissionEnabled(
    user,
    PERMISSIONS.PORTFOLIO_UNITS_MODE,
    PERMISSIONS.PORTFOLIOS_AI_STATEMENT_READER,
    PERMISSIONS.PORTFOLIOS_FILE_IMPORT,
    PERMISSIONS.TAX_ANALYSIS,
  );
};
